import { useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WineLookupKind } from '@gwc/shared';
import { lookupAdminApi } from '../../api/lookupAdmin';
import { lookupContext, type PickedLookup } from './setupHelpers';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

/**
 * Pick one entry of a lookup list by typing part of its name. Used for a
 * region's parent, a vineyard's or producer's region, and a merge survivor.
 * Searches the admin list, so entries awaiting review are offered too.
 */
export function LookupPicker({
  kind,
  label,
  value,
  onChange,
  excludeIds = [],
  placeholder = 'Type to search…',
  allowClear = true,
  clearLabel = 'None',
}: {
  kind: WineLookupKind;
  label: string;
  value: PickedLookup | null;
  onChange: (v: PickedLookup | null) => void;
  excludeIds?: string[];
  placeholder?: string;
  allowClear?: boolean;
  clearLabel?: string;
}) {
  const id = useId();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(!value);
  const debounced = useDebouncedValue(q.trim(), 250);
  // A value that arrives (or goes) from outside — the dialog filling its
  // draft, or a pick — decides whether the search box shows.
  const valueId = value?.id ?? null;
  useEffect(() => {
    setEditing(!valueId);
  }, [valueId]);

  const { data, isFetching } = useQuery({
    queryKey: ['lookupAdmin', kind, 'picker', debounced],
    queryFn: () => lookupAdminApi.list(kind, { q: debounced || undefined, pageSize: 8 }),
    enabled: editing,
    staleTime: 15_000,
  });
  const options = (data?.data ?? []).filter((o) => !excludeIds.includes(o.id));

  if (value && !editing) {
    return (
      <div>
        <p className="mb-1 block text-sm text-cream-200">{label}</p>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-ink-800 bg-ink-900 px-3 py-2">
          <span className="min-w-0 text-cream-50">
            {value.name}
            {value.parentName ? <span className="ml-2 text-xs text-cream-300/60">{value.parentName}</span> : null}
          </span>
          <span className="flex gap-3 text-xs">
            <button type="button" className="text-gold-300 hover:text-gold-200" onClick={() => setEditing(true)}>
              Change
            </button>
            {allowClear ? (
              <button type="button" className="text-cream-300/70 hover:text-cream-50" onClick={() => onChange(null)}>
                Clear
              </button>
            ) : null}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm text-cream-200">
        {label}
      </label>
      <input
        id={id}
        className="field"
        value={q}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className="mt-2 max-h-56 overflow-auto rounded-md border border-ink-800" aria-label={`${label} options`}>
        {allowClear && !value ? (
          <li className="border-b border-ink-800 px-3 py-2 text-xs text-cream-300/60">{clearLabel} (leave empty)</li>
        ) : null}
        {options.length === 0 ? (
          <li className="px-3 py-2 text-sm text-cream-300/60">{isFetching ? 'Searching…' : 'No matches.'}</li>
        ) : (
          options.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className="flex w-full flex-wrap items-baseline justify-between gap-x-3 px-3 py-2 text-left text-sm text-cream-100 hover:bg-ink-800 hover:text-gold-200"
                onClick={() => {
                  onChange({ id: o.id, name: o.name, parentName: o.parentName });
                  setEditing(false);
                  setQ('');
                }}
              >
                <span>
                  {o.name}
                  {o.needsReview ? <span className="ml-2 text-[10px] uppercase text-warning">awaiting review</span> : null}
                </span>
                <span className="text-xs text-cream-300/55">{lookupContext(o)}</span>
              </button>
            </li>
          ))
        )}
      </ul>
      {value ? (
        <button type="button" className="mt-2 text-xs text-cream-300/70 hover:text-cream-50" onClick={() => setEditing(false)}>
          Keep {value.name}
        </button>
      ) : null}
    </div>
  );
}
